import { z } from "zod";

/**
 * The slice of `AgentToolConfig.config` that says whether a voice agent must
 * ask the caller to confirm before this tool runs — the per-tool setting for
 * anything whose consequence the caller has to agree to (a payment, a booking).
 *
 * Absent is OFF: every row written before the key existed runs as it always
 * has, so no backfill is needed. A transfer does not read this key at all — it
 * is always confirmed, by its category, in the voice relay.
 *
 * DEGRADE, NEVER BLANK, as `dispatch.schema.ts` does: a malformed value drops to
 * OFF, and the rest of the blob passes through for whoever reads it next.
 */
export const AgentToolCallerConfirmationSchema = z
  .object({
    /** Ask the caller to confirm before running this tool on a voice call. */
    askCallerToConfirm: z.boolean().optional().catch(undefined)
  })
  .passthrough();

export type AgentToolCallerConfirmation = z.infer<typeof AgentToolCallerConfirmationSchema>;

/** Whether a stored `AgentToolConfig.config` blob asks for the caller's confirmation. Never throws. */
export function readAskCallerToConfirm(rawConfig: unknown): boolean {
  const parsed = AgentToolCallerConfirmationSchema.safeParse(rawConfig);
  return parsed.success && parsed.data.askCallerToConfirm === true;
}
